import React, { useState, useEffect, useMemo } from 'react';
import { 
  XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart, 
  CartesianGrid
} from 'recharts';
import { TrendingUp, TrendingDown, Clock, Calendar } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useTheme } from '../context/ThemeContext';
import { useChart } from '../context/ChartContext';

const PriceChart = () => {
  const { selectedCurrency, currencyConfig, exchangeRates, goldPriceUSD } = useCurrency();
  const { isDark, theme } = useTheme();
  const { selectedMetal, setSelectedMetal, selectedWeight, setSelectedWeight } = useChart();
  const [selectedPeriod, setSelectedPeriod] = useState('TODAY');
  const [chartData, setChartData] = useState([]);

  const config = currencyConfig[selectedCurrency];
  const rate = exchangeRates[selectedCurrency] || 1;

  const periods = [
    { key: 'LIVE', label: 'Live', points: 30, interval: 1, description: 'Last 30 minutes' },
    { key: 'TODAY', label: 'Today', points: 48, interval: 30, description: 'Last 24 hours' },
    { key: 'WEEK', label: 'Week', points: 84, interval: 120, description: 'Last 7 days' },
    { key: 'MONTH', label: 'Month', points: 30, interval: 1440, description: 'Last 30 days' },
    { key: '3MONTHS', label: '3 months', points: 90, interval: 1440, description: 'Last 3 months' },
    { key: '6MONTHS', label: '6 months', points: 180, interval: 1440, description: 'Last 6 months' },
    { key: 'YEAR', label: 'Year', points: 52, interval: 10080, description: 'Last 12 months' },
  ];

  const metals = [
    { key: 'GOLD', label: 'Gold', basePrice: goldPriceUSD || 4903.70, color: '#d4af37', bgColor: '#c9a227' },
    { key: 'SILVER', label: 'Silver', basePrice: 32.22, color: '#94a3b8', bgColor: '#64748b' },
    { key: 'PLATINUM', label: 'Platinum', basePrice: 1027.00, color: '#60a5fa', bgColor: '#3b82f6' },
    { key: 'PALLADIUM', label: 'Palladium', basePrice: 990.00, color: '#a78bfa', bgColor: '#8b5cf6' },
    { key: 'ELIZABETH', label: 'Elizabeth', basePrice: (goldPriceUSD || 4903.70) * 0.2354 * 1.08, color: '#fbbf24', bgColor: '#d97706' },
  ];

  const weights = [
    { key: 'gr', label: 'gr', multiplier: 1/31.1035 },
    { key: 'oz', label: 'oz', multiplier: 1 },
    { key: 'kilos', label: 'kilos', multiplier: 32.1507 },
  ];

  const currentMetal = metals.find(m => m.key === selectedMetal);
  const currentPeriod = periods.find(p => p.key === selectedPeriod);

  // Generate chart data
  useEffect(() => {
    const period = periods.find(p => p.key === selectedPeriod);
    const metal = metals.find(m => m.key === selectedMetal);
    const weight = weights.find(w => w.key === selectedWeight);
    if (!period || !metal || !weight) return;

    // Elizabeth doesn't use weight multiplier (it's a coin)
    const weightMultiplier = selectedMetal === 'ELIZABETH' ? 1 : weight.multiplier;
    const basePrice = metal.basePrice * rate * weightMultiplier;
    const data = [];
    const now = new Date();
    
    // Volatility based on period - increased for better visibility on low-priced items
    const baseVolatility = selectedPeriod === 'LIVE' ? 0.005 :
                       selectedPeriod === 'TODAY' ? 0.02 : 
                       selectedPeriod === 'WEEK' ? 0.05 : 
                       selectedPeriod === 'MONTH' ? 0.10 :
                       selectedPeriod === '3MONTHS' ? 0.15 :
                       selectedPeriod === '6MONTHS' ? 0.20 : 0.30;
    
    // Increase volatility for low-priced items to make chart movements visible
    const volatility = basePrice < 10 ? baseVolatility * 2 : 
                       basePrice < 100 ? baseVolatility * 1.5 : baseVolatility;

    let currentPrice = basePrice * (1 - volatility / 3);
    const trend = Math.random() > 0.45 ? 1 : -1;

    for (let i = period.points; i >= 0; i--) {
      const date = new Date(now);
      
      if (selectedPeriod === 'LIVE') {
        date.setMinutes(date.getMinutes() - i);
      } else if (selectedPeriod === 'TODAY') {
        date.setMinutes(date.getMinutes() - i * 30);
      } else if (selectedPeriod === 'WEEK') {
        date.setHours(date.getHours() - i * 2);
      } else {
        date.setDate(date.getDate() - i);
      }

      const range = basePrice * volatility / period.points * (Math.random() * 2 + 0.5);
      const direction = Math.random() > 0.48 ? 1 : -1;
      const change = direction * range * 0.5 + (trend * range * 0.1);
      
      currentPrice = Math.max(basePrice * 0.6, Math.min(basePrice * 1.4, currentPrice + change));

      const formatTime = () => {
        if (selectedPeriod === 'LIVE' || selectedPeriod === 'TODAY') {
          return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + ' EET';
        } else if (selectedPeriod === 'WEEK') {
          return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
        } else if (selectedPeriod === 'MONTH' || selectedPeriod === '3MONTHS' || selectedPeriod === '6MONTHS') {
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else {
          return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        }
      };

      data.push({
        time: formatTime(),
        price: parseFloat(currentPrice.toFixed(2)),
        fullDate: date.toISOString()
      });
    }

    setChartData(data);
  }, [selectedPeriod, selectedMetal, selectedWeight, selectedCurrency, rate, goldPriceUSD]);

  // Calculate stats
  const stats = useMemo(() => {
    if (chartData.length < 2) return { change: 0, percent: 0, isUp: true, max: 0, min: 0 };
    const first = chartData[0].price;
    const last = chartData[chartData.length - 1].price;
    const change = last - first;
    const percent = ((change / first) * 100).toFixed(2);
    const prices = chartData.map(d => d.price);
    
    return { 
      change: Math.abs(change).toFixed(2), 
      percent: Math.abs(percent).toFixed(2),
      isUp: change >= 0,
      max: Math.max(...prices).toFixed(2),
      min: Math.min(...prices).toFixed(2),
      current: last.toFixed(2),
      first: first.toFixed(2)
    };
  }, [chartData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div 
          className="rounded-lg px-4 py-3 shadow-lg border"
          style={{ backgroundColor: theme.tooltipBg, borderColor: theme.tooltipBorder }}
        >
          <p className="text-xs mb-1" style={{ color: theme.textMuted }}>{label}</p>
          <p className="font-mono font-bold text-lg" style={{ color: theme.tooltipText }}>
            {config.symbol}{payload[0].value.toLocaleString(config.locale, { minimumFractionDigits: 2 })}
          </p>
        </div>
      );
    }
    return null;
  };

  const getWeightLabel = () => {
    if (selectedMetal === 'ELIZABETH') return '(British Sovereign)';
    if (selectedWeight === 'oz') return 'per ounce';
    if (selectedWeight === 'gr') return 'per gram';
    return 'per kilo';
  };

  const metalColor = currentMetal?.color || '#d4af37';

  // Filter weights for Elizabeth (no weight options for coins)
  const availableWeights = selectedMetal === 'ELIZABETH' ? [] : weights;

  return (
    <div 
      id="price-chart"
      className="rounded-2xl border overflow-hidden shadow-lg mb-6 transition-colors duration-300"
      style={{ backgroundColor: theme.bgCard, borderColor: theme.border }}
    >
      {/* Header */}
      <div className="px-6 py-5 border-b" style={{ borderColor: theme.border }}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Left - Icon and price */}
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg"
              style={{ backgroundColor: currentMetal?.bgColor }}
            >
              {currentMetal?.label.substring(0, 2)}
            </div>
            
            <div>
              <h2 className="text-xl font-medium" style={{ color: theme.text }}>
                {selectedPeriod === 'LIVE' ? 'Live' : currentPeriod?.label} {currentMetal?.label.toLowerCase()} price {selectedCurrency} {getWeightLabel()}
              </h2>
              <div className="flex items-baseline gap-3 mt-1">
                <span className="text-3xl font-bold" style={{ color: theme.text }}>
                  {config.symbol}{parseFloat(stats.current).toLocaleString(config.locale, { minimumFractionDigits: 2 })}
                </span>
                <span className={`flex items-center gap-1 text-sm font-medium ${stats.isUp ? 'text-green-500' : 'text-red-500'}`}>
                  {stats.isUp ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {stats.isUp ? '+' : '-'}{config.symbol}{stats.change} ({stats.isUp ? '+' : '-'}{stats.percent}%)
                </span>
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm" style={{ color: theme.textMuted }}>
                <span>{selectedPeriod === 'LIVE' ? 'Live' : currentPeriod?.label}'s Maximum: <span className="font-medium" style={{ color: theme.text }}>{config.symbol}{parseFloat(stats.max).toLocaleString(config.locale)}</span></span>
                <span>{selectedPeriod === 'LIVE' ? 'Live' : currentPeriod?.label}'s Minimum: <span className="font-medium" style={{ color: theme.text }}>{config.symbol}{parseFloat(stats.min).toLocaleString(config.locale)}</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Control Bar */}
      <div className="px-6 py-4 border-b" style={{ backgroundColor: theme.bgTertiary, borderColor: theme.border }}>
        <div className="flex flex-wrap items-center gap-4">
          {/* Period */}
          <div className="flex items-center gap-2">
            <span className="text-sm" style={{ color: theme.textMuted }}>Period:</span>
            <div className="flex items-center gap-1">
              {periods.map(period => (
                <button
                  key={period.key}
                  onClick={() => setSelectedPeriod(period.key)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedPeriod === period.key
                      ? period.key === 'LIVE' 
                        ? isDark ? 'bg-gray-700 text-white' : 'bg-gray-800 text-white'
                        : isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'
                      : isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {period.key === 'LIVE' && <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-1.5 animate-pulse" />}
                  {period.label}
                </button>
              ))}
            </div>
          </div>

          {/* Metal */}
          <div className="flex items-center gap-2">
            <span className="text-sm" style={{ color: theme.textMuted }}>Metal:</span>
            <select 
              value={selectedMetal}
              onChange={(e) => setSelectedMetal(e.target.value)}
              className="px-3 py-1.5 rounded-full border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-yellow-500"
              style={{ backgroundColor: theme.bgInput, borderColor: theme.border, color: theme.text }}
            >
              {metals.map(metal => (
                <option key={metal.key} value={metal.key}>{metal.label}</option>
              ))}
            </select>
          </div>

          {/* Weight - Only show if not Elizabeth */}
          {availableWeights.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm" style={{ color: theme.textMuted }}>Weight:</span>
              <div className="flex items-center gap-1">
                {availableWeights.map(w => (
                  <button
                    key={w.key}
                    onClick={() => setSelectedWeight(w.key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                      selectedWeight === w.key
                        ? isDark ? 'bg-gray-700 text-white border-transparent' : 'bg-gray-800 text-white border-transparent'
                        : isDark ? 'border-gray-600 text-gray-400 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="p-4 h-[400px] relative" style={{ backgroundColor: theme.chartBg }}>
        {/* Watermark */}
        <div className="absolute top-6 left-6 text-sm font-medium z-10" style={{ color: isDark ? '#333' : '#ddd' }}>
          LetsGold<span style={{ color: isDark ? '#444' : '#ccc' }}>.io</span>
        </div>

        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
            <defs>
              <linearGradient id={`chartGradient-${selectedMetal}-${isDark ? 'dark' : 'light'}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={metalColor} stopOpacity={isDark ? 0.6 : 0.4}/>
                <stop offset="50%" stopColor={metalColor} stopOpacity={isDark ? 0.3 : 0.2}/>
                <stop offset="100%" stopColor={metalColor} stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.chartGrid} horizontal={true} vertical={false} />
            <XAxis 
              dataKey="time" 
              axisLine={{ stroke: theme.chartGrid }}
              tickLine={false}
              tick={{ fill: theme.chartText, fontSize: 11 }}
              interval="preserveStartEnd"
            />
            <YAxis 
              domain={['auto', 'auto']}
              axisLine={false}
              tickLine={false}
              tick={{ fill: theme.chartText, fontSize: 11 }}
              tickFormatter={(value) => `${value.toLocaleString()}`}
              width={80}
              orientation="right"
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="price"
              stroke={metalColor}
              strokeWidth={2}
              fillOpacity={1}
              fill={`url(#chartGradient-${selectedMetal}-${isDark ? 'dark' : 'light'})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Bottom Date Range */}
      <div className="px-6 py-3 border-t flex items-center justify-between text-sm" style={{ backgroundColor: theme.bgTertiary, borderColor: theme.border, color: theme.textMuted }}>
        <div className="flex items-center gap-2">
          {selectedPeriod === 'LIVE' && (
            <>
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span>Updating every 5 seconds</span>
            </>
          )}
          {selectedPeriod !== 'LIVE' && (
            <>
              <Calendar className="w-4 h-4" />
              <span>{currentPeriod?.description}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" />
          <span>
            {chartData.length > 0 && (
              <>
                {chartData[0]?.fullDate ? new Date(chartData[0].fullDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                {' → '}
                {chartData[chartData.length - 1]?.fullDate ? new Date(chartData[chartData.length - 1].fullDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
              </>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PriceChart;
